import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import type {InspectionDeviceHistoryEntryResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {AttachmentGallery} from 'src/pages/Modules/Inspections/shared/AttachmentGallery';
import {useGetInspectionDeviceHistoryQuery} from 'src/redux/queries/inspection';
import {
  ComplianceStateEnumPill,
  InspectionStateEnumPill,
} from 'src/shared/constants/PillConstants';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {formatUtcShortDate} from 'src/shared/utils/formatters';

export interface DeviceHistoryListProps {
  deviceId: number;
  noItemsText: string;
}

/** Prior inspection history for a device — shared by the record accordion and the device detail page. */
export const DeviceHistoryList: React.FC<DeviceHistoryListProps> = ({
  deviceId,
  noItemsText,
}) => {
  const {data, isFetching} = useGetInspectionDeviceHistoryQuery({deviceId});

  if (isFetching) {
    return (
      <Center py="lg">
        <Loader size="sm" />
      </Center>
    );
  }

  const entries = [...(data ?? [])].sort((a, b) => {
    const aTime = a.inspectionDate
      ? parseUtcDate(a.inspectionDate).getTime()
      : 0;
    const bTime = b.inspectionDate
      ? parseUtcDate(b.inspectionDate).getTime()
      : 0;
    return bTime - aTime;
  });

  if (entries.length === 0) {
    return <NoItemsPanel noItemsText={noItemsText} />;
  }

  return (
    <Stack gap="sm">
      {entries.map(entry => (
        <DeviceHistoryEntry key={entry.inspectionId} entry={entry} />
      ))}
    </Stack>
  );
};

/** Renders one history entry's full record — reused by `DeviceHistoryList` and the record-flow history drawer (`DeviceHistorySidebar`). */
export const DeviceHistoryEntry: React.FC<{
  entry: InspectionDeviceHistoryEntryResponse;
}> = ({entry}) => {
  return (
    <Stack
      gap={4}
      p="sm"
      style={{border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4}}>
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={600} size="sm">
            {entry.inspectionTypeName}
          </Text>
          <Text size="xs" c="dimmed">
            {formatUtcShortDate(entry.inspectionDate, 'Undated')}
          </Text>
        </Group>
        <Group gap="xs">
          <Pill {...InspectionStateEnumPill(entry.inspectionState)} />
          <Pill {...ComplianceStateEnumPill(entry.complianceState)} />
        </Group>
      </Group>

      {entry.inspectorName && (
        <Text size="xs" c="dimmed">
          Inspector: {entry.inspectorName}
        </Text>
      )}

      {entry.comments && (
        <Text size="sm" c="dimmed">
          {entry.comments}
        </Text>
      )}

      {entry.values.length > 0 && (
        <Stack gap={2}>
          {entry.values.map(value => (
            <Group key={value.formFieldInvariantId} gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed" style={{minWidth: 140}}>
                {value.label}
              </Text>
              <Text size="xs">
                {value.displayValue ?? value.value ?? '—'}
                {value.unit ? ` ${value.unit}` : ''}
              </Text>
            </Group>
          ))}
        </Stack>
      )}

      <AttachmentGallery attachments={entry.attachments} />
    </Stack>
  );
};
